import React from 'react';
import { IdCard, Award, ShieldCheck, FileCheck, AlertTriangle, FileText } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { requirements } from '../data/mock';

const iconMap = {
  'id-card': IdCard,
  'award': Award,
  'shield-check': ShieldCheck,
  'file-check': FileCheck,
};

const RequirementsSection = () => {
  return (
    <section id="requirements" className="py-20 lg:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block bg-sky-100 text-sky-700 px-4 py-2 rounded-full text-sm font-semibold mb-4">
            Please Read
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-4">
            Lease Requirements
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            To lease this vehicle, you must meet the following criteria.
          </p>
        </div>

        {/* Requirements Grid */}
        <div className="grid md:grid-cols-2 gap-6 lg:gap-8 mb-12">
          {requirements.map((req, index) => {
            const IconComponent = iconMap[req.icon];
            return (
              <div
                key={index}
                className="flex gap-5 bg-slate-50 rounded-2xl p-6 lg:p-8 hover:shadow-lg transition-all duration-300 border border-slate-100 hover:border-sky-200"
              >
                <div className="w-14 h-14 bg-sky-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  {IconComponent && <IconComponent className="w-7 h-7 text-sky-600" />}
                </div>
                <div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2">{req.title}</h3>
                  <p className="text-slate-600 leading-relaxed">{req.description}</p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Critical Insurance Notice */}
        <div className="bg-gradient-to-r from-red-600 to-red-700 rounded-3xl p-8 lg:p-10 text-white">
          <div className="flex flex-col lg:flex-row lg:items-start gap-6">
            <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center flex-shrink-0">
              <AlertTriangle className="w-8 h-8 text-white" />
            </div>
            <div>
              <h3 className="text-2xl font-bold mb-3">Important: User-Insured Vehicle</h3>
              <p className="text-red-100 mb-4 text-lg">
                The user is responsible for insuring this vehicle during the lease period.
              </p>
              <ul className="space-y-2 text-red-100">
                <li className="flex items-start gap-2">
                  <span className="text-white font-bold">•</span>
                  We do NOT provide self-drive hire insurance
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-white font-bold">•</span>
                  You must arrange fully comprehensive insurance for this specific vehicle
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-white font-bold">•</span>
                  Insurance must cover commercial window-cleaning use
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-white font-bold">•</span>
                  Proof of valid insurance required before collection – no exceptions
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Contract Link */}
        <div className="mt-8 bg-slate-100 rounded-2xl p-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-sky-100 rounded-xl flex items-center justify-center">
              <FileText className="w-6 h-6 text-sky-600" />
            </div>
            <div>
              <h4 className="font-bold text-slate-900">Lease Agreement</h4>
              <p className="text-sm text-slate-600">View our standard short-term vehicle lease agreement</p>
            </div>
          </div>
          <Link to="/contract">
            <Button className="bg-sky-600 hover:bg-sky-700 text-white">
              View Contract
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default RequirementsSection;
